/**
 * Session lifecycle manager — founder ruling 2026-09-27.
 *
 *  • Refresh keeps you signed in ONLY while the session is still valid
 *    (server-checked, never restored off localStorage presence alone).
 *  • After SESSION_INACTIVITY_MS of inactivity the session expires and the
 *    user must authenticate again. The last-activity timestamp is persisted,
 *    so closing/reopening the browser can NOT silently restore an expired
 *    session.
 *  • Logout clears everything immediately (see utils/logout.js).
 *  • The JWT itself hard-expires server-side (12h absolute lifetime).
 *  • Never armed for signed-out visitors — no timers on the Gateway/landing.
 */

const SESSION_INACTIVITY_MS = 8 * 60 * 60 * 1000; // 8h provider/customer inactivity
const WARNING_MS = 2 * 60 * 1000; // warn 2 minutes before expiry
const ACTIVITY_KEY = 'session_last_activity';
const TOKEN_KEYS = ['access_token', 'auth_token', 'token'];

// Every key that represents authenticated/privileged state. Cleared as one
// unit so a half-logged-in "glitch" state can never survive.
const AUTH_KEYS = [
  ...TOKEN_KEYS,
  'user_data', 'analytics_user', 'user_id', 'user_name', 'user_role',
  'user_roles', 'company_id', 'terms_accepted', 'hasCompletedOnboarding',
  'activation_state', 'signup_in_progress', 'first_login_pending',
  'owner_session_backup', 'owner_impersonating_worker',
  'super_admin_session', 'sa_session_id',
  ACTIVITY_KEY,
];

let timeoutId = null;
let warningTimeoutId = null;
let watchdogId = null;
let running = false;
let lastArmAt = 0;
let onWarningCallback = null;
let onLogoutCallback = null;

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

const getToken = () => {
  for (const k of TOKEN_KEYS) {
    try { const v = localStorage.getItem(k); if (v) return v; } catch { return null; }
  }
  return null;
};

const hasUserBlob = () => {
  try { return !!(localStorage.getItem('user_data') || localStorage.getItem('analytics_user')); }
  catch { return false; }
};

const clearSession = () => {
  try { AUTH_KEYS.forEach((k) => localStorage.removeItem(k)); } catch { /* ignore */ }
  try { sessionStorage.removeItem('session_id'); } catch { /* ignore */ }
};

const touchActivity = () => {
  try { localStorage.setItem(ACTIVITY_KEY, String(Date.now())); } catch { /* ignore */ }
};

const isInactivityExpired = () => {
  let raw = null;
  try { raw = localStorage.getItem(ACTIVITY_KEY); } catch { /* ignore */ }
  if (!raw) { touchActivity(); return false; }
  const last = parseInt(raw, 10);
  if (Number.isNaN(last)) { touchActivity(); return false; }
  return Date.now() - last > SESSION_INACTIVITY_MS;
};

/**
 * Server-validated restore check. Returns 'none' | 'valid' | 'expired'.
 * 'expired' also means the stale local session was already cleared.
 */
const validateStoredSession = async () => {
  const token = getToken();
  if (!token) return 'none';
  if (isInactivityExpired()) { clearSession(); return 'expired'; }
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/session-valid`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) { clearSession(); return 'expired'; }
    return 'valid';
  } catch {
    return 'valid'; // network failure — never lock out an offline user
  }
};

const logout = () => {
  // Re-check the persisted timestamp: activity in another tab keeps the
  // session alive across all tabs.
  if (!isInactivityExpired()) { resetTimeout(); return; }
  stopSessionTimeout();
  clearSession();
  if (onLogoutCallback) onLogoutCallback();
  else window.location.href = '/login';
};

const showWarning = () => {
  if (isInactivityExpired() === false && Date.now() - (parseInt(localStorage.getItem(ACTIVITY_KEY) || '0', 10)) < SESSION_INACTIVITY_MS - WARNING_MS) {
    resetTimeout(); // activity happened elsewhere — no warning needed
    return;
  }
  if (onWarningCallback) onWarningCallback();
};

const resetTimeout = () => {
  if (timeoutId) clearTimeout(timeoutId);
  if (warningTimeoutId) clearTimeout(warningTimeoutId);
  const existingWarning = document.getElementById('session-timeout-warning');
  if (existingWarning) existingWarning.remove();
  warningTimeoutId = setTimeout(showWarning, SESSION_INACTIVITY_MS - WARNING_MS);
  timeoutId = setTimeout(logout, SESSION_INACTIVITY_MS);
};

// Throttled: persist activity + re-arm timers at most every 30s.
const onActivity = () => {
  const now = Date.now();
  if (now - lastArmAt < 30 * 1000) return;
  lastArmAt = now;
  touchActivity();
  resetTimeout();
};

const initSessionTimeout = (options = {}) => {
  onWarningCallback = options.onWarning || null;
  onLogoutCallback = options.onLogout || null;
  stopSessionTimeout(); // idempotent — safe to re-run on route changes

  const token = getToken();
  let legacyBlob = null;
  try { legacyBlob = localStorage.getItem('analytics_user'); } catch { /* ignore */ }

  if (!token && !legacyBlob) {
    // Orphaned cache (user blob without any token) must never fake a login.
    try { localStorage.removeItem('user_data'); } catch { /* ignore */ }
    return; // signed out — no timers, no warnings
  }

  const arm = () => {
    touchActivity();
    lastArmAt = Date.now();
    resetTimeout();
    ACTIVITY_EVENTS.forEach((e) => document.addEventListener(e, onActivity, true));
    // Watchdog covers sleep/wake + multi-tab: checks the persisted timestamp.
    watchdogId = setInterval(() => { if (isInactivityExpired()) logout(); }, 60 * 1000);
    running = true;
  };

  if (token && hasUserBlob()) {
    validateStoredSession().then((state) => {
      if (state === 'valid') arm();
      // 'expired' → already cleared silently; route guard handles redirects.
    });
  } else if (legacyBlob) {
    // Legacy analytics_user-only session (no JWT): local inactivity only.
    if (isInactivityExpired()) { clearSession(); return; }
    arm();
  }
};

const stopSessionTimeout = () => {
  if (timeoutId) clearTimeout(timeoutId);
  if (warningTimeoutId) clearTimeout(warningTimeoutId);
  if (watchdogId) clearInterval(watchdogId);
  timeoutId = warningTimeoutId = watchdogId = null;
  ACTIVITY_EVENTS.forEach((e) => document.removeEventListener(e, onActivity, true));
  running = false;
};

export {
  initSessionTimeout,
  stopSessionTimeout,
  resetTimeout,
  clearSession,
  validateStoredSession,
  touchActivity,
};
